<template>
  <component
    :is="phosphorComponent"
    v-if="phosphorComponent"
    class="base-icon"
    :class="[
      { inline, 'is-spinner': id === 'spinner', 'flip-horizontal': flipHorizontal },
      justify ? `justify-${justify}` : null,
    ]"
    :color="color"
    :size="computedSize"
    :weight="weight"
  />
</template>

<script setup lang="ts">
import { type Component, computed, shallowRef, watch } from 'vue';

import { iconResolver } from '../../runtime/iconRegistry';
import { isModuleLoadError } from '../../runtime/moduleLoadError';

export interface BaseIconProps {
  id?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  inline?: boolean;
  flipHorizontal?: boolean;
  weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
  justify?: 'start' | 'center' | 'end' | null;
}

const props = withDefaults(defineProps<BaseIconProps>(), {
  id: 'bullet',
  size: 'md',
  color: 'currentColor',
  inline: false,
  flipHorizontal: false,
  weight: 'regular',
  justify: null,
});

const sizeMapping = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

const computedSize = computed(() => {
  if (props.size) {
    return sizeMapping[props.size];
  }
  return 24;
});

const phosphorComponent = shallowRef<Component | null>(null);

let seq = 0;

const loadIconComponent = async (iconId: string) => {
  const thisSeq = ++seq;

  const attempt = async (): Promise<void> => {
    const mod = await iconResolver(iconId);
    if (thisSeq !== seq) return;
    phosphorComponent.value = mod.default || mod;
  };

  try {
    await attempt();
  } catch (firstErr) {
    if (thisSeq !== seq) return;

    if (isModuleLoadError(firstErr)) {
      await new Promise(r => setTimeout(r, 300));
      if (thisSeq !== seq) return;

      try {
        await attempt();
      } catch (secondErr) {
        if (thisSeq !== seq) return;
        // Module-load failures are deploy-time chunk staleness, not missing icons.
        // The app's preload-error plugin owns the reload; we just render nothing.
        if (isModuleLoadError(secondErr)) {
          phosphorComponent.value = null;
          return;
        }
        console.warn(`Icon "${iconId}" not found:`, secondErr);
        phosphorComponent.value = null;
      }
    } else {
      console.warn(`Icon "${iconId}" not found:`, firstErr);
      phosphorComponent.value = null;
    }
  }
};

watch(
  () => props.id,
  newId => {
    loadIconComponent(newId!);
  },
  { immediate: true },
);
</script>

<style>
.base-icon {
  display: block;
  flex-shrink: 0;
  &.inline {
    display: inline-block;
    vertical-align: bottom;
    margin-right: 0.5ch;
  }
  &.is-spinner {
    transform-origin: center;
    animation: spin 1.5s var(--transition-ease) infinite;
    opacity: 0.75;
    will-change: transform;
  }
  &.flip-horizontal {
    --base-icon-scale-x: -1;
    transform-origin: center;
    transform: scaleX(var(--base-icon-scale-x));
  }
  &.justify-start {
    margin-inline-end: auto;
  }
  &.justify-center {
    margin-inline: auto;
  }
  &.justify-end {
    margin-inline-start: auto;
  }
}

@keyframes spin {
  0% {
    transform: scaleX(var(--base-icon-scale-x, 1)) rotate(0deg);
  }
  100% {
    transform: scaleX(var(--base-icon-scale-x, 1)) rotate(360deg);
  }
}
</style>
